import React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../components/ui/input-group";
import type { Omittable } from "../../lib/props";

type ExpandableSearchSize = "xs" | "sm" | "default";

type ExpandableSearchProps = Omittable<{
  onValueChange: (value: string) => void;
  placeholder?: string;
  size?: ExpandableSearchSize;
}>;

// Collapsed trigger footprint per size, and the input size the expanded field
// runs at (the base Input has three sizes; xs and sm both land on `sm`).
const TRIGGER_SIZE: Record<ExpandableSearchSize, "icon-xs" | "icon-sm" | "icon"> = {
  xs: "icon-xs",
  sm: "icon-sm",
  default: "icon",
};
const INPUT_SIZE: Record<ExpandableSearchSize, "sm" | "default"> = {
  xs: "sm",
  sm: "sm",
  default: "default",
};

// The memo takes React's own shallow compare. A custom comparator that skipped
// `onValueChange` also skipped the render that refreshes `onValueChangeRef`, so
// a callback closing over changing state (a filter object, a page cursor) kept
// reporting against the state it was born with — and only unstuck itself if the
// placeholder or size happened to change too.
export const ExpandableSearch = React.memo(
  function ExpandableSearch({
    onValueChange,
    placeholder = "Search...",
    size = "sm",
  }: ExpandableSearchProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const hasValueRef = React.useRef(false);
    const justClearedRef = React.useRef(false);
    // Keeps `handleValueChange` (and everything built on it) referentially
    // stable while still reporting through the newest callback.
    const onValueChangeRef = React.useRef(onValueChange);
    onValueChangeRef.current = onValueChange;
    const [expanded, setExpanded] = React.useState(false);
    const [value, setValue] = React.useState("");

    const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

    const handleValueChange = React.useCallback((v: string) => {
      const hadValue = hasValueRef.current;
      hasValueRef.current = v.length > 0;
      setValue(v);

      // Value was cleared via the clear button — stay expanded and re-focus
      if (hadValue && v.length === 0) {
        justClearedRef.current = true;
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          justClearedRef.current = false;
        });
      }

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        React.startTransition(() => onValueChangeRef.current(v));
      }, 250);
    }, []);

    React.useEffect(() => () => clearTimeout(debounceRef.current), []);

    const collapse = React.useCallback(() => {
      if (!hasValueRef.current && !justClearedRef.current) setExpanded(false);
    }, []);

    const handleClick = React.useCallback(() => {
      if (!expanded) setExpanded(true);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }, [expanded]);

    // Keyboard navigation: Tab away collapses the search
    const handleBlur = React.useCallback(() => {
      collapse();
    }, [collapse]);

    // Escape clears a populated field, then collapses an empty one.
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Escape") return;
        if (hasValueRef.current) {
          handleValueChange("");
          return;
        }
        setExpanded(false);
      },
      [handleValueChange],
    );

    React.useEffect(() => {
      if (!expanded) return;

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }, [expanded]);

    // Collapse when clicking outside the container
    React.useEffect(() => {
      if (!expanded) return;

      const handleMouseUp = (e: MouseEvent) => {
        const node = containerRef.current;
        if (node && !node.contains(e.target as Node)) {
          requestAnimationFrame(() => collapse());
        }
      };

      document.addEventListener("mouseup", handleMouseUp);
      return () => document.removeEventListener("mouseup", handleMouseUp);
    }, [expanded, collapse]);

    // Toolbar search is always a pill: circular collapsed trigger,
    // rounded-full field — matching the pill toolbar chips.
    return (
      <Box ref={containerRef} direction="row" align="center">
        {expanded ? (
          <InputGroup className="w-48 rounded-full">
            <InputGroupAddon>
              <Icon name="search" />
            </InputGroupAddon>
            <InputGroupInput
              ref={inputRef}
              type="search"
              size={INPUT_SIZE[size]}
              placeholder={placeholder}
              value={value}
              onValueChange={handleValueChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
            {value ? (
              <InputGroupAddon align="inline-end">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="rounded-full"
                  aria-label="Clear search"
                  onClick={() => handleValueChange("")}
                >
                  <Icon name="x" />
                </Button>
              </InputGroupAddon>
            ) : null}
          </InputGroup>
        ) : (
          <Button
            variant="ghost"
            size={TRIGGER_SIZE[size]}
            className="rounded-full"
            aria-label="Search"
            onClick={handleClick}
          >
            <Icon name="search" />
          </Button>
        )}
      </Box>
    );
  },
);

ExpandableSearch.displayName = "ExpandableSearch";
